/**
 * SharedNavSocialMenu — accessible "Social" dropdown for SharedNav.
 *
 * Replaces the flat "Social" nav link with a tap/click-to-expand
 * submenu that lists the two split-page entries:
 *   * "Just Plucking Around" → /social/just-plucking-around
 *   * "Community" → /social/community
 *
 * Behavior contract (both variants):
 *   * Click / tap on trigger toggles the menu.
 *   * `Escape` closes.
 *   * Outside `mousedown` closes.
 *   * Route change closes (via `useLocation`).
 *   * `ArrowDown` on trigger opens and focuses the first item;
 *     `Enter` / `Space` on trigger open + focus first item.
 *   * `ArrowDown` / `ArrowUp` inside the menu cycles item focus.
 *   * `Home` / `End` inside the menu jumps to first/last.
 *   * Menu items are real `<Link>`s (react-router) — no synthetic
 *     navigate. Each has `role="menuitem"`.
 *
 * ARIA:
 *   * Trigger button has `aria-haspopup="menu"`,
 *     `aria-expanded={open}`, `aria-controls={menuId}`.
 *   * Menu panel has `role="menu"` and an `aria-label` describing
 *     the submenu.
 *
 * Family-L discipline:
 *   * Colors: `--pp-slate` (default), `--pp-orange-hover` (hover /
 *     focus). No inline hex.
 *   * Border + surface: `border-pp-line` + `bg-pp-v3-surface` from
 *     Tailwind theme tokens.
 *
 * Variants:
 *   * `variant="desktop"` — absolute-positioned popover under the
 *     trigger. Used in the desktop center-nav cluster.
 *   * `variant="mobile"` — inline-expanded panel (block flow),
 *     stacked children fill the drawer. Used inside the mobile
 *     Sheet drawer.
 */
import React, { useEffect, useId, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown } from "lucide-react";

export const SOCIAL_MENU_ITEMS = [
  {
    label: "Just Plucking Around",
    to: "/social/just-plucking-around",
    testId: "shared-nav-social-item-just-plucking-around",
  },
  {
    label: "Community",
    to: "/social/community",
    testId: "shared-nav-social-item-community",
  },
];

export default function SharedNavSocialMenu({
  variant = "desktop",
  onItemClick,
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const itemsRef = useRef([]);
  const menuId = useId();
  const location = useLocation();

  // Outside click + Escape close.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Close on route change.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const focusItem = (idx) => {
    const el = itemsRef.current[idx];
    if (el && typeof el.focus === "function") el.focus();
  };

  const openAndFocusFirst = () => {
    setOpen(true);
    // Focus after the menu is in the DOM.
    setTimeout(() => focusItem(0), 0);
  };

  const onTriggerKeyDown = (e) => {
    if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      openAndFocusFirst();
    }
  };

  const onMenuKeyDown = (e) => {
    const count = itemsRef.current.length;
    const current = itemsRef.current.indexOf(document.activeElement);
    if (e.key === "ArrowDown") {
      e.preventDefault();
      focusItem((current + 1 + count) % count);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      focusItem((current - 1 + count) % count);
    } else if (e.key === "Home") {
      e.preventDefault();
      focusItem(0);
    } else if (e.key === "End") {
      e.preventDefault();
      focusItem(count - 1);
    }
  };

  const isMobile = variant === "mobile";

  const triggerClass = isMobile
    ? "flex w-full items-center justify-between text-[17px] font-semibold py-3 px-1 rounded-md transition-colors"
    : "inline-flex items-center gap-1 text-[15px] font-semibold transition-colors";

  const menuClass = isMobile
    ? "mt-1 mb-1 flex flex-col rounded-md"
    : "absolute left-1/2 top-full z-50 mt-2 -translate-x-1/2 min-w-[240px] rounded-xl border border-pp-line py-2 shadow-lg";

  const itemClass = isMobile
    ? "block w-full text-left text-[15px] font-semibold py-3 pl-5 pr-2 rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pp-orange"
    : "block px-4 py-2 text-[15px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pp-orange";

  return (
    <div
      ref={rootRef}
      className={isMobile ? "block w-full" : "relative"}
      data-testid={`shared-nav-social-root-${variant}`}
    >
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => (open ? setOpen(false) : openAndFocusFirst())}
        onKeyDown={onTriggerKeyDown}
        className={triggerClass}
        style={{ color: "var(--pp-slate)", background: "transparent" }}
        onMouseEnter={(e) => (e.currentTarget.style.color = "var(--pp-orange-hover)")}
        onMouseLeave={(e) => (e.currentTarget.style.color = "var(--pp-slate)")}
        data-testid={`shared-nav-social-trigger-${variant}`}
      >
        <span>Social</span>
        <ChevronDown
          className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
      </button>
      {open && (
        <div
          id={menuId}
          role="menu"
          aria-label="Social submenu"
          onKeyDown={onMenuKeyDown}
          className={menuClass}
          style={
            isMobile
              ? { background: "transparent" }
              : { background: "var(--pp-v3-surface)" }
          }
          data-testid={`shared-nav-social-menu-${variant}`}
        >
          {SOCIAL_MENU_ITEMS.map((item, idx) => (
            <Link
              key={item.to}
              to={item.to}
              role="menuitem"
              ref={(el) => {
                itemsRef.current[idx] = el;
              }}
              className={itemClass}
              style={{ color: "var(--pp-slate)" }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "var(--pp-orange-hover)")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "var(--pp-slate)")}
              onClick={() => {
                setOpen(false);
                if (typeof onItemClick === "function") onItemClick();
              }}
              data-testid={item.testId}
            >
              {item.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
